import { filterDOMProps } from '@react-aria/utils';
import type { DOMProps } from '@react-types/shared';
import type { ReactNode } from 'react';
import { createContext, forwardRef } from 'react';
import type { ContextValue } from 'react-aria-components';
import { useSlottedContext } from 'react-aria-components';

import { getDisplayName, isDefuseChainId } from '@infinex/chain-config';
import type { ChainKey, DefuseChainId } from '@infinex/chain-config';
import { CHAIN_SVG_REFS, DEFUSE_SVG_REFS } from '@infinex/ui-assets';
import {
  cn,
  styleVars,
  useStyleProps,
  useContextProps,
} from '@infinex/ui-styles';
import type { BaseStyleProps, UnsafeStyleProps } from '@infinex/ui-styles';

import { EMBLEM_DIMENSIONS, type EmblemSize } from './constants';
import type { ChainAssetFallback } from './fallback';
import { ChainFallbackMark } from './fallback';

export type ChainEmblemProps = {
  /**
   * The chain glyph to display. Chains delivered at runtime (via the
   * per-user `chains` dataset) may not be in the bundled `ChainKey` union;
   * unknown keys render the fallback mark instead of a bundled asset.
   */
  of: ChainKey | DefuseChainId | (string & {});
  /** Colors and name for the fallback mark shown when `of` has no bundled asset. */
  fallback?: ChainAssetFallback;
  /**
   * The size of the emblem.
   *
   * @default medium
   */
  size?: EmblemSize;
  /**
   * When true, the emblem is decorative (usually because of adjacent text) and
   * will not be visible to screen readers.
   *
   * @default false
   */
  isDecorative?: boolean;
} & DOMProps &
  BaseStyleProps &
  UnsafeStyleProps;

type ChainEmblemContextValue = Omit<ChainEmblemProps, 'of'> & {
  /**
   * Wrap the element with some custom behaviour, within a slotted component.
   */
  render?: (element: ReactNode) => ReactNode;
};

export const ChainEmblemContext = createContext<
  ContextValue<ChainEmblemContextValue, HTMLDivElement>
>({});

/**
 * A symbolic image that serves as the _canonical_ graphic representation of a
 * chain.
 *
 * [Taurus docs: `ChainEmblem`](https://ui-sb.app.infinex.dev/?path=/docs/components-chainemblem--docs)
 */
export const ChainEmblem = forwardRef<HTMLDivElement, ChainEmblemProps>(
  function ChainEmblem(props, ref) {
    [props, ref] = useContextProps(props, ref, ChainEmblemContext);
    const ctx = useSlottedContext(ChainEmblemContext);
    const { className, style } = useStyleProps(props);

    const { isDecorative, fallback, size = 'medium', ...otherProps } = props;
    const sizeData = EMBLEM_DIMENSIONS[size];

    // Undefined for chain keys without a bundled asset (e.g. chains delivered
    // at runtime) — those render the fallback mark.
    const refs = isDefuseChainId(props.of)
      ? DEFUSE_SVG_REFS[props.of]
      : (CHAIN_SVG_REFS[props.of as ChainKey] as
          | (typeof CHAIN_SVG_REFS)[ChainKey]
          | undefined);

    const fallbackName = fallback?.name ?? String(props.of);
    const displayName = refs
      ? getDisplayName(props.of as ChainKey | DefuseChainId)
      : fallbackName;

    const element = (
      <div
        {...filterDOMProps(otherProps)}
        ref={ref}
        role={isDecorative ? 'presentation' : 'img'}
        aria-label={isDecorative ? undefined : displayName}
        className={cn(
          'shrink-0 contain-content',
          'size-(--size) rounded-(--radius)',
          '[&_svg]:size-full',
          className
        )}
        style={styleVars({
          ...style,
          '--radius': `${sizeData.radius}px`,
          '--size': `${sizeData.size}px`,
        })}
      >
        {refs ? (
          refs.emblemRef
        ) : (
          <ChainFallbackMark fallback={fallback} name={fallbackName} />
        )}
      </div>
    );

    if (ctx?.render) {
      return ctx.render(element);
    }

    return element;
  }
);
